import { OverlaySpinner } from '@ankr.com/ui';
import { ReactNode } from 'react';
import { Navigate } from 'react-router';

import { AccountPermission } from 'modules/permissions/constants';
import { GuardAccountPermission } from 'modules/account/components/GuardAccountPermission';
import { NoSSRGuard } from 'modules/common/components/NoSSRGuard';
import { Placeholder } from 'modules/common/components/Placeholder';
import { chainsRoutesConfig } from 'domains/chains/routes';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAccountStatus } from 'modules/accountStatus/hooks/useAccountStatus';
import { useAuth } from 'modules/auth/hooks/useAuth';

export interface BillingAccessGuardProps {
  children: ReactNode;
  fallback: ReactNode;
}

const chainsPath = chainsRoutesConfig.chains.generatePath();

export const BillingAccessGuard = ({
  children,
  fallback,
}: BillingAccessGuardProps) => {
  const { authToken, isLoggedIn, isLoggingIn } = useAuth();
  const { isAccountInitializing } = useAccount();
  const { initalLoading: accountStatusInitialLoading, isEnterprise } =
    useAccountStatus();

  const hasAuthToken = Boolean(authToken);
  const shouldLogIn = hasAuthToken && !isLoggedIn;

  const loading =
    isLoggingIn ||
    shouldLogIn ||
    isAccountInitializing ||
    accountStatusInitialLoading;

  return (
    <NoSSRGuard>
      <Placeholder hasPlaceholder={loading} placeholder={<OverlaySpinner />}>
        <Placeholder
          hasPlaceholder={!isLoggedIn}
          placeholder={<Navigate to={chainsPath} />}
        >
          <Placeholder hasPlaceholder={isEnterprise} placeholder={fallback}>
            <GuardAccountPermission
              permission={AccountPermission.Billing}
              shouldHideAlert
              shouldRedirect
            >
              {children}
            </GuardAccountPermission>
          </Placeholder>
        </Placeholder>
      </Placeholder>
    </NoSSRGuard>
  );
};
